import { useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { LanguageCode } from 'src/i18n/types'
import { getRegionalConfigApi } from '../apiUtils'
import { detectRegionFromUrl, validLanguages } from '../commonUtils'

const tldToOpenStreetMapCountryCodes: Record<string, string> = {
  uk: 'gb',
  de: 'de',
  nl: 'nl',
  be: 'be',
  lu: 'lu',
}

// Map language codes to display names for future use
const languageDisplayNames: Record<string, string> = {
  uk: 'United Kingdom',
  de: 'Germany',
  nl: 'Netherlands',
  be: 'Belgium',
  lu: 'Luxembourg',
}

// Map languages to their associated regions (for future API calls)
const languageToRegionsMap: Record<string, string[]> = {
  uk: ['LON', 'MID', 'MAN', 'SCO', 'DEV'],
  de: ['DUT'],
  nl: ['NOR'],
  be: ['BNL'], // Benelux
  lu: ['LUX'],
}

interface RegionContextValue {
  // Core properties used by the app
  region: string | null
  setRegion: React.Dispatch<React.SetStateAction<string | null>>

  // Future use - not accessed by current app code
  language: string
  setLanguage: React.Dispatch<React.SetStateAction<string>>
  languageDisplayNames: Record<string, string>
  validLanguages: string[]
  getLanguageDisplayName: () => string
  isValidLanguage: (languageCode: string) => boolean
  changeLanguage: (newLanguage: string) => void
  getAvailableRegions: () => string[]
  changeDbRegion: (newRegion: string) => void

  // Regional config
  regionalConfig: unknown
  regionalConfigLoading: boolean
  regionalConfigError: unknown
  connectedRegions: string[]
  urlDefaultLanguage: string
  openStreetMapCountryCodes: string[]
}

const defaultValue: RegionContextValue = {
  region: 'LON',
  setRegion: (() => {}) as RegionContextValue['setRegion'],
  language: 'uk',
  setLanguage: (() => {}) as RegionContextValue['setLanguage'],
  languageDisplayNames,
  validLanguages: validLanguages as unknown as string[],
  getLanguageDisplayName: () => 'Unknown',
  isValidLanguage: () => false,
  changeLanguage: () => {},
  getAvailableRegions: () => [],
  changeDbRegion: () => {},
  regionalConfig: undefined,
  regionalConfigLoading: false,
  regionalConfigError: undefined,
  connectedRegions: [],
  urlDefaultLanguage: LanguageCode.English,
  openStreetMapCountryCodes: [],
}

const RegionContext = createContext<RegionContextValue>(defaultValue)

export const RegionProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()

  // Set initial region based on subdomain or TLD
  const [region, setRegion] = useState<string | null>(() => detectRegionFromUrl() as string | null)

  // Secondary state for future language support - not used by current app code
  const [language, setLanguage] = useState(() => detectRegionFromUrl() || 'uk')

  // Fetch regional config using react-query (once per session)
  const {
    data: regionalConfig,
    isLoading: regionalConfigLoading,
    error: regionalConfigError,
  } = useQuery({
    queryKey: ['regionalConfig'],
    queryFn: getRegionalConfigApi,
    staleTime: 1000 * 60 * 60 * 24, // 1 day
  })

  // State for connectedRegions
  const [connectedRegions, setConnectedRegions] = useState<string[]>([])
  const [openStreetMapCountryCodes, setOpenStreetMapCountryCodes] = useState<string[]>([])
  const [urlDefaultLanguage, setUrlDefaultLanguage] = useState(LanguageCode.English)

  const regionCode = useMemo(() => detectRegionFromUrl(), [])

  useEffect(() => {
    let supportedTlds: string[] = []

    if (regionalConfig && Array.isArray(regionalConfig)) {
      const configEntry = regionalConfig.find((entry: any) => entry?.tld === regionCode)

      if (configEntry && Array.isArray(configEntry.regions)) {
        const normalizedLanguage =
          configEntry.defaultLanguage === 'uk' ? LanguageCode.English : configEntry.defaultLanguage
        setUrlDefaultLanguage(normalizedLanguage)
        setConnectedRegions(configEntry.regions)
        supportedTlds = [configEntry.tld]
      } else {
        setConnectedRegions([])
      }

      if (_.isEmpty(supportedTlds)) {
        supportedTlds = regionalConfig.map((entry: any) => entry?.tld)
      }

      setOpenStreetMapCountryCodes(
        supportedTlds
          .filter((tld) => tld !== '.com')
          .map((tld) => tldToOpenStreetMapCountryCodes[tld]),
      )
    } else {
      setConnectedRegions([])
    }
  }, [regionalConfig, regionCode])

  useEffect(() => {
    // Update region if URL changes
    setRegion(detectRegionFromUrl() as string | null)
  }, [location.pathname])

  // Get the display name for the current language
  const getLanguageDisplayName = () => languageDisplayNames[language] || 'Unknown'

  // Check if a given language code is valid
  const isValidLanguage = (languageCode: string) =>
    validLanguages.includes(languageCode.toLowerCase())

  // Change the current language (placeholder for future use)
  const changeLanguage = (newLanguage: string) => {
    if (isValidLanguage(newLanguage)) setLanguage(newLanguage)
  }

  // Get available regions for the current language
  const getAvailableRegions = () => languageToRegionsMap[language] || []

  // Change the current region (within the same language)
  const changeDbRegion = (newRegion: string) => {
    if ((languageToRegionsMap[language] || []).includes(newRegion)) {
      setRegion(newRegion)
    }
  }

  return (
    <RegionContext.Provider
      value={{
        // Core properties used by the app
        region,
        setRegion,

        // Future use - not accessed by current app code
        language,
        setLanguage,
        languageDisplayNames,
        validLanguages,
        getLanguageDisplayName,
        isValidLanguage,
        changeLanguage,
        getAvailableRegions,
        changeDbRegion,

        // Regional config
        regionalConfig,
        regionalConfigLoading,
        regionalConfigError,
        connectedRegions,
        urlDefaultLanguage,
        openStreetMapCountryCodes,
      }}
    >
      {children}
    </RegionContext.Provider>
  )
}

export const useRegion = (): RegionContextValue => {
  const context = useContext(RegionContext)
  if (!context) throw new Error('useRegion must be used within a RegionProvider')
  return context
}
